import Seo from "@/components/Seo";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, X } from "lucide-react";
import { useMemo } from "react";
import { CLIENTS } from "@/data/clients";
import { ALL_INDUSTRIES, FLAGSHIP_INDUSTRIES } from "@/data/industries";
import UKMap from "@/components/UKMap";

// Match a client to an industry by fuzzy label check against badges + hiServices
const clientMatchesIndustry = (client, industryLabel) => {
  const needle = industryLabel.toLowerCase();
  const haystack = [
    ...(client.badges || []).map(([, label]) => label),
    ...(client.hiServices || []).map(([, label]) => label),
    client.description || "",
    client.hiActivity || "",
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
};

export default function Clients() {
  const [params, setParams] = useSearchParams();
  const industrySlug = params.get("industry");

  const industry = useMemo(() => {
    if (!industrySlug) return null;
    return (
      [...FLAGSHIP_INDUSTRIES, ...ALL_INDUSTRIES].find(
        (i) => i.slug === industrySlug
      ) || null
    );
  }, [industrySlug]);

  const filteredClients = useMemo(() => {
    if (!industry) return CLIENTS;
    return CLIENTS.filter((c) => clientMatchesIndustry(c, industry.label));
  }, [industry]);

  const clearFilter = () => {
    const next = new URLSearchParams(params);
    next.delete("industry");
    setParams(next, { replace: true });
  };

  return (
    <main data-testid="clients-page" className="bg-white">
      <Seo
        title="Our clients"
        description="An interactive map of every business HighwaysIndustry.com works with across the UK - from national contractors to specialist manufacturers, laboratories and traffic management firms."
        path="/clients"
      />
      {/* Header */}
      <section className="pt-32 md:pt-40 pb-8 md:pb-14">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-accent-blue">
              Our Clients
            </span>
          </div>
          <div className="mt-4 grid lg:grid-cols-12 gap-8 items-end">
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              data-testid="clients-headline"
              className="lg:col-span-8 font-heading font-black text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tighter text-slate-900"
            >
              The businesses
              <br />
              <span className="text-accent-blue italic">
                we help get seen.
              </span>
            </motion.h1>
            <p className="lg:col-span-4 text-lg text-slate-600 leading-relaxed">
              Some of the highways and infrastructure brands we work with , 
              they trust HighwaysIndustry.com with their content, positioning
              and reach. Explore where they operate, and click through to
              see how we work with each of them.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#all-clients"
              data-testid="hero-browse-all-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-blue text-white font-heading font-semibold hover:bg-cyan-500 transition-colors text-sm"
            >
              Browse all clients
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Map */}
      <section
        data-testid="map-section"
        className="dark-section relative bg-slate-950 py-16 md:py-24 overflow-hidden"
      >
        {/* Subtle grid backdrop */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #22d3ee 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 text-white">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-cyan-300">
                National footprint
              </span>
            </div>
            <h2 className="mt-4 font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              From Cornwall to
              <br />
              <span className="text-accent-blue italic">Glasgow.</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              Hover a pin to see the client. Click it to open their profile.
              Multi-client sites show a count marker.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              <div>
                <div className="font-heading font-black text-3xl md:text-4xl text-white">
                  100+
                </div>
                <div className="text-xs uppercase tracking-widest text-white/50 mt-1">
                  Industry Clients
                </div>
              </div>
              <div>
                <div className="font-heading font-black text-3xl md:text-4xl text-white">
                  12+ Yrs
                </div>
                <div className="text-xs uppercase tracking-widest text-white/50 mt-1">
                  Supporting Clients
                </div>
              </div>
              <div>
                <div className="font-heading font-black text-3xl md:text-4xl text-white">
                  3,000+
                </div>
                <div className="text-xs uppercase tracking-widest text-white/50 mt-1">
                  Industry Articles Published
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <UKMap />
          </div>
        </div>
      </section>

      {/* All clients directory */}
      <section id="all-clients" className="py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex flex-col items-center text-center gap-4 mb-12">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                Client Directory
              </span>
              <span className="h-px w-12 bg-accent-blue" />
            </div>
            <h2 className="font-heading font-black text-3xl md:text-5xl text-slate-900">
              {industry ? `${industry.label} clients` : "All clients"}
            </h2>
            {industry ? (
              <div
                data-testid="industry-filter-pill"
                className="mt-2 inline-flex items-center gap-3 px-5 py-2 rounded-full bg-slate-900 text-white text-sm font-heading font-semibold"
              >
                <span>
                  Filtered by{" "}
                  <span className="text-accent-blue">{industry.label}</span>
                </span>
                <button
                  type="button"
                  onClick={clearFilter}
                  data-testid="industry-filter-clear"
                  aria-label="Clear industry filter"
                  className="opacity-70 hover:opacity-100 transition-opacity"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <a
                href="#all-clients"
                data-testid="all-clients-btn"
                className="mt-2 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-blue text-white font-heading font-semibold hover:bg-cyan-500 transition-colors text-sm"
              >
                Browse all clients
                <ArrowUpRight size={14} />
              </a>
            )}
            <p className="text-sm text-slate-500 mt-1">
              {filteredClients.length}{" "}
              {filteredClients.length === 1 ? "client" : "clients"}
              {industry ? " in this industry" : ""}
            </p>
          </div>

          {filteredClients.length === 0 ? (
            <div className="text-center py-16 max-w-2xl mx-auto">
              <p className="text-slate-700 text-lg">
                No clients listed for{" "}
                <span className="font-semibold">{industry?.label}</span> yet.
              </p>
              <p className="mt-3 text-slate-500 text-sm">
                Are you a business in this sector?{" "}
                <Link
                  to="/contact"
                  className="text-accent-blue underline underline-offset-4"
                >
                  Get in touch to be featured
                </Link>
                , or{" "}
                <button
                  type="button"
                  onClick={clearFilter}
                  className="text-accent-blue underline underline-offset-4"
                >
                  browse all clients
                </button>
                .
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredClients.map((c, i) => (
                <motion.div
                  key={c.slug}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{
                    duration: 0.4,
                    delay: Math.min(i * 0.02, 0.4),
                  }}
                >
                  <Link
                    to={`/clients/${c.slug}`}
                    data-testid={`client-card-${c.slug}`}
                    className="group block h-full p-5 rounded-xl border border-slate-200 bg-white hover:border-accent-blue hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-heading font-bold text-lg text-slate-900 leading-tight group-hover:text-accent-blue transition-colors">
                        {c.name}
                      </h3>
                      <ArrowUpRight
                        className="text-slate-300 group-hover:text-accent-blue transition-colors flex-shrink-0"
                        size={18}
                      />
                    </div>
                    <div className="mt-3 flex items-start gap-2 text-xs text-slate-500">
                      <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">
                        {c.postcode} ·{" "}
                        {c.address.split(",").pop().trim()}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
